import React from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Step, Question } from '../../types';
import StepColumn from './StepColumn';
import QuestionCard from './QuestionCard';

interface KanbanBoardProps {
  steps: Step[];
  questions: Question[];
  activeStepId: number | null;
  onQuestionsUpdate: (updatedQuestions: Question[]) => void;
  onEditQuestion: (question: Question) => void;
  onDeleteQuestion: (question: Question) => void;
  onAddQuestion: (stepNumber: number) => void;
  onInsertQuestion: (stepNumber: number, insertAtOrder: number) => void;
  onReorderQuestion: (questionId: number, newStepNumber: number, newOrder: number) => Promise<void>;
  deletingQuestionId?: number | null;
  isOperationLoading?: boolean;
  isPresentMode?: boolean;
  isReadOnly?: boolean;
  // Client-specific props
  isClientView?: boolean;
  userResponses?: Array<{
    id: number;
    user_id: number;
    template_id: number;
    question_id: number;
    response_text: string;
    interval: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    created_at: string;
    updated_at: string;
  }>;
  onAddResponse?: (questionId: number, responseText: string, interval: string) => Promise<void>;
  onEditResponse?: (responseId: number, responseText: string, interval: string) => Promise<void>;
  onDeleteResponse?: (responseId: number) => Promise<void>;
  onQuestionClick?: (questionId: number, questionOrder: number) => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  steps,
  questions,
  activeStepId,
  onQuestionsUpdate,
  onEditQuestion,
  onDeleteQuestion,
  onAddQuestion,
  onInsertQuestion,
  onReorderQuestion,
  deletingQuestionId,
  isOperationLoading = false,
  isPresentMode = false,
  isReadOnly = false,
  isClientView = false,
  userResponses = [],
  onAddResponse,
  onEditResponse,
  onDeleteResponse,
  onQuestionClick,
}) => {
  const [activeQuestion, setActiveQuestion] = React.useState<Question | null>(null);
  const [originalQuestion, setOriginalQuestion] = React.useState<Question | null>(null);
  const [isDragActive, setIsDragActive] = React.useState(false);

  // Predefined gradients for steps
  // const stepGradients = [
  //   'linear-gradient(135deg, #667eea, #764ba2)', // Purple-blue
  //   'linear-gradient(135deg, #f093fb, #f5576c)', // Pink-red
  //   'linear-gradient(135deg, #4facfe, #00f2fe)', // Blue-cyan
  //   'linear-gradient(135deg, #43e97b, #38f9d7)', // Green-cyan
  //   'linear-gradient(135deg, #fa709a, #fee140)', // Pink-yellow
  // ];

  const stepGradients = [
    '#DFF4D8', // Light green
    '#CAEDBD', // Lighter green
    '#9EDD87', // Medium green
    '#7DD35F', // Darker green
    '#58BE34', // Dark green
  ];

  // Function to determine text color based on background color
  const getTextColor = (stepNumber: number): string => {
    // For the lighter greens (steps 1-3), use dark text
    // For the darker greens (steps 4-5), use white text
    return stepNumber <= 3 ? '#1F2937' : '#FFFFFF'; // gray-800 : white
  };

  const getStepGradient = (stepNumber: number): string => {
    return stepGradients[(stepNumber - 1) % stepGradients.length];
  };

  // Group questions by step number
  const getQuestionsForStep = (stepNumber: number): Question[] => {
    return questions
      .filter(q => q.step_number === stepNumber)
      .sort((a, b) => a.question_order - b.question_order);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeData = active.data.current;
    
    if (activeData?.type === 'question') {
      const question = activeData.question;
      setActiveQuestion(question);
      setIsDragActive(true);
      // Store the original question state to compare later
      setOriginalQuestion({
        ...question,
        step_number: question.step_number,
        question_order: question.question_order,
      });
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over || !active.data.current) return;

    const activeQuestion = active.data.current.question as Question;
    const overData = over.data.current;

    // Handle dropping over a step column
    if (overData?.type === 'step') {
      const targetStep = overData.step as Step;
      
      if (activeQuestion.step_number !== targetStep.step_number) {
        const updatedQuestions = questions.map(q => {
          if (q.id === activeQuestion.id) {
            // Calculate new order for the question in the target step
            const targetStepQuestions = getQuestionsForStep(targetStep.step_number);
            const newOrder = targetStepQuestions.length + 1;
            
            return {
              ...q,
              step_number: targetStep.step_number,
              question_order: newOrder,
            };
          }
          return q;
        });
        
        onQuestionsUpdate(updatedQuestions);
      }
    }

    // Handle dropping over another question
    if (overData?.type === 'question') {
      const overQuestion = overData.question as Question;
      
      if (activeQuestion.id !== overQuestion.id) {
        const activeStepQuestions = getQuestionsForStep(activeQuestion.step_number);
        const overStepQuestions = getQuestionsForStep(overQuestion.step_number);
        
        // Same step reordering
        if (activeQuestion.step_number === overQuestion.step_number) {
          const oldIndex = activeStepQuestions.findIndex(q => q.id === activeQuestion.id);
          const newIndex = activeStepQuestions.findIndex(q => q.id === overQuestion.id);
          
          if (oldIndex !== newIndex) {
            const reorderedQuestions = arrayMove(activeStepQuestions, oldIndex, newIndex);
            
            // Update question orders
            const updatedQuestions = questions.map(q => {
              if (q.step_number === activeQuestion.step_number) {
                const newOrderIndex = reorderedQuestions.findIndex((rq: Question) => rq.id === q.id);
                if (newOrderIndex !== -1) {
                  return { ...q, question_order: newOrderIndex + 1 };
                }
              }
              return q;
            });
            
            onQuestionsUpdate(updatedQuestions);
          }
        }
        // Different step movement
        else {
          const targetIndex = overStepQuestions.findIndex(q => q.id === overQuestion.id);
          
          const updatedQuestions = questions.map(q => {
            if (q.id === activeQuestion.id) {
              return {
                ...q,
                step_number: overQuestion.step_number,
                question_order: targetIndex + 1,
              };
            }
            // Adjust orders for questions in the target step
            if (q.step_number === overQuestion.step_number && q.question_order >= targetIndex + 1) {
              return { ...q, question_order: q.question_order + 1 };
            }
            return q;
          });
          
          onQuestionsUpdate(updatedQuestions);
        }
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active } = event;
    
    setIsDragActive(false);
    
    if (!active.data.current || !originalQuestion) {
      setActiveQuestion(null);
      setOriginalQuestion(null);
      return;
    }

    const draggedQuestionId = originalQuestion.id;
    const currentQuestion = questions.find(q => q.id === draggedQuestionId);
    
    if (currentQuestion) {
      // Check if the question's step or order has changed from the original
      const hasChanged = 
        currentQuestion.step_number !== originalQuestion.step_number ||
        currentQuestion.question_order !== originalQuestion.question_order;
        
      if (hasChanged) {
        try {
          await onReorderQuestion(
            currentQuestion.id,
            currentQuestion.step_number,
            currentQuestion.question_order
          );
        } catch (error) {
          console.error('Failed to reorder question:', error);
          // Optionally revert the UI changes here
        }
      }
    }
    
    setActiveQuestion(null);
    setOriginalQuestion(null);
  };

  const boardContent = (
    <div className="flex-1">
      {isPresentMode ? (
        // Present Mode: Show all steps in kanban layout
        <div className="flex gap-4">
          {steps.map((step) => {
            const stepQuestions = getQuestionsForStep(step.step_number);
            return (
              <div key={step.id} className="flex-1 min-w-0">
                <StepColumn
                  step={step}
                  questions={stepQuestions}
                  stepGradient={getStepGradient(step.step_number)}
                  stepTextColor={getTextColor(step.step_number)}
                  onEditQuestion={onEditQuestion}
                  onDeleteQuestion={onDeleteQuestion}
                  onAddQuestion={onAddQuestion}
                  onInsertQuestion={onInsertQuestion}
                  deletingQuestionId={deletingQuestionId}
                  isOperationLoading={isOperationLoading}
                  isSingleStepView={false}
                  isDragActive={isDragActive}
                  isReadOnly={isReadOnly}
                  isPresentMode={isPresentMode}
                  isClientView={isClientView}
                  userResponses={userResponses}
                  onAddResponse={onAddResponse}
                  onEditResponse={onEditResponse}
                  onDeleteResponse={onDeleteResponse}
                  onQuestionClick={onQuestionClick}
                />
              </div>
            );
          })}
        </div>
      ) : (
        // Prep Mode: Show only active step
        (() => {
          // Find the active step and its questions
          const activeStep = steps.find(step => step.step_number === activeStepId);
          if (!activeStep) return null;

          const stepQuestions = getQuestionsForStep(activeStep.step_number);
          
          return (
            <div className="h-full flex flex-col">
              <StepColumn
                key={activeStep.id}
                step={activeStep}
                questions={stepQuestions}
                stepGradient={getStepGradient(activeStep.step_number)}
                stepTextColor={getTextColor(activeStep.step_number)}
                onEditQuestion={onEditQuestion}
                onDeleteQuestion={onDeleteQuestion}
                onAddQuestion={onAddQuestion}
                onInsertQuestion={onInsertQuestion}
                deletingQuestionId={deletingQuestionId}
                isOperationLoading={isOperationLoading}
                isSingleStepView={true}
                isDragActive={isDragActive}
                isReadOnly={isReadOnly}
                isPresentMode={isPresentMode}
                isClientView={isClientView}
                userResponses={userResponses}
                onAddResponse={onAddResponse}
                onEditResponse={onEditResponse}
                onDeleteResponse={onDeleteResponse}
                onQuestionClick={onQuestionClick}
              />
            </div>
          );
        })()
      )}
    </div>
  );

  return isReadOnly ? (
    boardContent
  ) : (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {boardContent}

      <DragOverlay>
        {activeQuestion ? (
          <QuestionCard
            question={activeQuestion}
            index={0}
            stepNumber={activeQuestion.step_number}
            onEdit={() => {}}
            onDelete={() => {}}
            onInsertAt={() => {}}
            isDeleting={false}
            showInsertButton={false}
            isDragActive={true}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;